import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getProject, saveExisting, updateProject } from '../data/projects';
import {
  ROUTES, ROUTE_STATUS, VALUE_STATUS, CONFIDENCE_LABEL, ALL_FIELDS, FIELD_GROUPS,
  EX_VERSION, exPropKey, buildExisting, executeRoute, consolidate, buildSchematic,
  ELEMENT_TYPES, completionState, exLogEvent, fieldDef, routeById, fetchBuildingFootprint,
  routeAnswerCounts, requiredProgress, routesForField,
  FACADES, buildElements, orientationProgress, isFacadePlaceable,
} from '../data/existing';
import { propertySignature } from '../data/identification';
import { uploadFile } from '../data/upload';
import Roadmap, { reachedStageFor } from '../components/Roadmap';
import AgentActivity from '../components/AgentActivity';
import DocumentArchive from '../components/DocumentArchive';
import ConfirmDialog from '../components/ConfirmDialog';
import NoteDialog from '../components/NoteDialog';
import './ExistingCondition.css';

// Stage 04 — "ניתוח מצב קיים" (chunk 04). Runs six inference routes over the
// evidence, consolidates their observations into one selected value per field,
// and assembles the result into a schematic of the existing building.
const ACTIVE_STAGE = 3;
// What still blocks finishing, in one sentence.
function blockReason(completion, progress) {
  const parts = [];
  if (!completion.atLeastOneRun) parts.push('טרם הופעל אף מסלול');
  if (progress.missing.length) {
    parts.push(progress.missing.length === 1
      ? `חסר נתון חובה: ${progress.missing[0].displayName}`
      : `חסרים ${progress.missing.length} נתוני חובה`);
  }
  if (completion.unresolvedConflicts.length) {
    parts.push(completion.unresolvedConflicts.length === 1
      ? `יש להכריע בסתירה ב«${completion.unresolvedConflicts[0].displayName}»`
      : `יש להכריע ב-${completion.unresolvedConflicts.length} סתירות`);
  }
  if (!completion.schematicReady) parts.push('סכמת הבניין טרם נבנתה');
  return `לא ניתן לסיים את השלב — ${parts.join(' · ')}. ניתן לשמור טיוטה ולחזור.`;
}

const SAVE_FAILED = 'שמירת השלב נכשלה — השינויים האחרונים לא נשמרו בשרת. בדקו את החיבור ונסו שוב.';

function Pill({ tone, children }) {
  return <span className={`ex-pill ex-pill-${tone}`}>{children}</span>;
}

const fmt = (v, unit) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'כן' : 'לא';
  const n = Number(v);
  const s = Number.isNaN(n) ? String(v) : n.toLocaleString('he-IL');
  return unit ? `${s} ${unit}` : s;
};

// ─── Route documents ────────────────────────────────────────────────────────
// §11 — a document added from inside a route also enters the project archive,
// and removing it from the route never deletes it from the archive.
function RouteDocuments({ route, archiveDocs, onAddDoc, onRemoveDoc }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const docs = route.documents || [];
  return (
    <div className="ex-route-docs">
      <div className="ex-route-docs-head">
        <h5><i className="fi fi-rr-document" /> מסמכים ששימשו את המסלול</h5>
      </div>
      {docs.length === 0 ? (
        <p className="ex-route-docs-empty">לא צורפו מסמכים למסלול זה.</p>
      ) : (
        <ul className="ex-route-docs-list">
          {docs.map((d, i) => (
            <li key={i}>
              {d.link
                ? <a href={d.link} target="_blank" rel="noreferrer">{d.name}</a>
                : <span>{d.name}</span>}
              {d.uploadFailed && <span className="ex-doc-failed">ההעלאה נכשלה — הקובץ לא נשמר</span>}
              <button type="button" className="ex-doc-x" onClick={() => onRemoveDoc(route.id, i)}>
                <i className="fi fi-rr-cross-small" /> הסר מהמסלול
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="ex-route-docs-add">
        <label className="ex-doc-pick">
          <span><i className="fi fi-rr-folder" /> בחירה מארכיון</span>
          <select
            value=""
            onChange={(e) => {
              const d = archiveDocs[Number(e.target.value)];
              if (d) onAddDoc(route.id, { name: d.name, link: d.link });
              e.target.value = '';
            }}
          >
            <option value="" disabled>בחרו מסמך…</option>
            {archiveDocs.map((d, i) => <option key={i} value={i}>{d.name}</option>)}
          </select>
        </label>
        <button type="button" className="np-btn np-btn-ghost" disabled={busy} onClick={() => fileRef.current?.click()}>
          <i className="fi fi-rr-cloud-upload" /> {busy ? 'מעלה…' : 'העלאת מסמך'}
        </button>
        <input
          ref={fileRef}
          type="file"
          hidden
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            setBusy(true);
            let link = null;
            let failed = false;
            try { link = await uploadFile(file); } catch { failed = true; }
            onAddDoc(route.id, { name: file.name, link, uploaded: true, uploadFailed: failed });
            setBusy(false);
          }}
        />
      </div>
    </div>
  );
}

// ─── Manual route (§7.1) ────────────────────────────────────────────────────
// The only route that can produce values without an external source: the user
// types them. Everything entered here is User Input and is marked as such.
function ManualEntry({ fields, vals, onChange, onSubmit }) {
  // 24 identical inputs give no clue which ones matter; default to the ones that
  // are still blocking the stage, with an escape hatch to see everything.
  const [onlyNeeded, setOnlyNeeded] = useState(true);
  // The draft lives on the SCREEN, not here: this component unmounts whenever
  // the panel collapses (opening any other route did it), and it used to be
  // re-seeded from route.run on every table-side edit — both silently threw
  // away whatever the user had typed.
  const set = (id, v) => onChange({ ...vals, [id]: v });

  // What the consolidation already holds for each field, and from where.
  const state = useMemo(() => {
    const m = {};
    for (const f of fields || []) {
      m[f.fieldId] = {
        answered: f.selectedValue !== null,
        byOther: f.selectedValue !== null && f.selectedRouteId && f.selectedRouteId !== 'manual_input',
        routeName: f.selectedRouteId ? routeById(f.selectedRouteId)?.name : null,
        required: f.required,
      };
    }
    return m;
  }, [fields]);

  const needed = (f) => !state[f.id]?.answered || Boolean(vals[f.id]);
  const filled = Object.values(vals).filter((v) => String(v).trim() !== '').length;
  const reqFilled = ALL_FIELDS.filter((f) => f.required && String(vals[f.id] ?? '').trim() !== '').length;
  const reqTotal = ALL_FIELDS.filter((f) => f.required).length;
  const hiddenCount = ALL_FIELDS.filter((f) => f.routes.includes('manual_input') && !needed(f)).length;

  return (
    <div className="ex-manual">
      <p className="ex-manual-intro">
        הזינו את הנתונים הידועים לכם. כל ערך שיוזן כאן יסומן כ־User Input — הזנת משתמש ללא מקור תומך.
      </p>
      <div className="ex-manual-filter">
        <label>
          <input type="checkbox" checked={onlyNeeded} onChange={(e) => setOnlyNeeded(e.target.checked)} />
          הצג רק נתונים שעדיין חסרים
        </label>
        {onlyNeeded && hiddenCount > 0 && (
          <span className="ex-manual-hidden">{hiddenCount} נתונים כבר נענו על ידי מסלול אחר ומוסתרים</span>
        )}
      </div>

      {onlyNeeded && !ALL_FIELDS.some((f) => f.routes.includes('manual_input') && needed(f)) && (
        <p className="ex-manual-done">כל הנתונים שמסלול זה יכול לספק כבר נענו. הסירו את הסינון כדי לערוך אותם.</p>
      )}
      {FIELD_GROUPS.map((g) => {
        const shown = g.fields
          .filter((f) => f.routes.includes('manual_input'))
          .filter((f) => !onlyNeeded || needed(f));
        if (!shown.length) return null;
        return (
          <div key={g.id} className="ex-manual-group">
            <h6>{g.title}</h6>
            <div className="ex-manual-grid">
              {shown.map((f) => {
                const s = state[f.id] || {};
                return (
                  <label key={f.id} className={`ex-manual-field ${s.byOther ? 'is-answered' : ''}`}>
                    <span>
                      {f.name}{f.unit ? ` (${f.unit})` : ''}
                      {f.required && <span className="ex-req" title="נתון חובה">*</span>}
                    </span>
                    {f.type === 'bool' ? (
                      <select value={vals[f.id] ?? ''} onChange={(e) => set(f.id, e.target.value)}>
                        <option value="">—</option>
                        <option value="true">כן</option>
                        <option value="false">לא</option>
                      </select>
                    ) : (
                      <input
                        type={f.type === 'number' ? 'number' : 'text'}
                        value={vals[f.id] ?? ''}
                        onChange={(e) => set(f.id, e.target.value)}
                        placeholder="—"
                      />
                    )}
                    {s.byOther && (
                      <span className="ex-manual-note">
                        כבר נענה על ידי {s.routeName} — הזנה כאן תתחרה בערך הקיים
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="ex-manual-foot">
        <span className="ex-manual-count">
          {filled} נתונים מולאו · {reqFilled} מתוך {reqTotal} נתוני חובה
        </span>
        <button type="button" className="np-btn np-btn-primary" disabled={!filled} onClick={() => onSubmit(vals)}>
          <i className="fi fi-rr-check" /> שמירת הנתונים והפעלת המסלול
        </button>
      </div>
    </div>
  );
}

// ─── One route (§5 closed row, §6 open structure) ───────────────────────────
function RouteRow({ route, fields, isOpen, onToggle, onRun, onAddDoc, onRemoveDoc, onManual, manualDraft, onManualChange, archiveDocs, running }) {
  const st = ROUTE_STATUS[route.status] || ROUTE_STATUS.not_run;
  const obs = route.run?.observations || [];
  const counts = routeAnswerCounts(route);
  // Manual entry, and any document route still missing its evidence, open their
  // panel instead of running — there is nothing to run against yet.
  const isManual = route.id === 'manual_input';
  const opensPanel = isManual || (route.kind === 'document' && !(route.documents || []).length);
  // "הרצה מחדש" is the wrong verb for a form the user fills in themselves.
  // "השלמת מסמכים" on an AUTO route described something the button cannot do —
  // it has no document area to open; it simply has no data to run against.
  const autoNoInput = route.kind === 'auto' && route.status === 'missing_docs';
  const actionLabel = isManual
    ? (route.run ? 'עריכת הנתונים' : 'הזנת נתונים')
    : (autoNoInput ? 'אין נתונים להרצה' : st.action);
  const actionIcon = isManual ? 'fi-rr-pencil' : (opensPanel ? 'fi-rr-cloud-upload' : (route.run ? 'fi-rr-refresh' : 'fi-rr-play'));
  return (
    <div className={`ex-route ${isOpen ? 'is-open' : ''}`} data-route-id={route.id}>
      <div className="ex-route-row" role="row">
        <span className="ex-route-toggle" aria-hidden="true">
          <i className={`fi ${isOpen ? 'fi-rr-angle-small-down' : 'fi-rr-angle-small-left'}`} />
        </span>
        <span className="ex-route-num">{route.num}.</span>
        {/* One labelled disclosure instead of a text button beside an unlabelled
            chevron doing the same thing — two tab stops, one of which announced
            only "button, collapsed". */}
        <button type="button" className="ex-route-name" onClick={() => onToggle(route.id)} aria-expanded={isOpen}>
          {route.name}
        </button>
        <span className="ex-route-gaps">
          {counts.total ? `ענה על ${counts.answered} מתוך ${counts.total}` : '—'}
        </span>
        <span className="ex-route-status"><Pill tone={st.tone}>{st.label}</Pill></span>
        <button
          type="button"
          className="np-btn np-btn-secondary ex-route-run"
          disabled={running === route.id || autoNoInput}
          onClick={() => (opensPanel ? onToggle(route.id, true) : onRun(route.id))}
        >
          {running === route.id
            ? <><span className="rp-spinner" /> מריץ…</>
            : <><i className={`fi ${actionIcon}`} /> {actionLabel}</>}
        </button>
      </div>

      {isOpen && (
        <div className="ex-route-body">
          <p className="ex-route-explain">{route.explain}</p>
          <dl className="ex-route-meta">
            <div><dt>קלט</dt><dd>{route.inputs}</dd></div>
            <div><dt>מגבלות</dt><dd>{route.limits}</dd></div>
          </dl>

          {route.id === 'manual_input' ? (
            <ManualEntry fields={fields} vals={manualDraft} onChange={onManualChange} onSubmit={onManual} />
          ) : (
            <>
              <div className="ex-obs">
                <div className="ex-obs-row ex-obs-head">
                  <span>שדה</span><span>ערך</span><span>מקור</span><span>ודאות</span><span>סטטוס</span>
                </div>
                {obs.length === 0 ? (
                  <p className="ex-obs-empty">המסלול טרם הופעל.</p>
                ) : obs.map((o, i) => (
                  <div className="ex-obs-row" key={i}>
                    <span className="ex-obs-name">
                      {o.displayName}
                      {fieldDef(o.fieldId)?.required && <span className="ex-req" title="נתון חובה">*</span>}
                    </span>
                    <span className="ex-obs-val">{fmt(o.value, o.unit)}</span>
                    <span className="ex-obs-src" title={o.source?.label || ''}>
                      {o.source?.link
                        ? <a href={o.source.link} target="_blank" rel="noreferrer">{o.source.label}</a>
                        : (o.source?.label || '—')}
                    </span>
                    <span className="ex-obs-conf">{(CONFIDENCE_LABEL[o.confidence] || CONFIDENCE_LABEL.missing).label}</span>
                    <span className="ex-obs-status">
                      {o.value === null
                        ? <Pill tone="warn">לא נמצא במסלול</Pill>
                        : <Pill tone="ok">נצפה</Pill>}
                    </span>
                    {/* The note is where a route explains itself — why it can't
                        answer, or what caveat applies. It was never rendered. */}
                    {o.note && <span className="ex-obs-note">{o.note}</span>}
                  </div>
                ))}
              </div>
              <RouteDocuments route={route} archiveDocs={archiveDocs} onAddDoc={onAddDoc} onRemoveDoc={onRemoveDoc} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Conflict resolution (§8/§12). A row that only *announces* a contradiction is
// a dead end: the competing values were truncated into a caption and the only
// way to decide was to realise the route dropdown was the decision. This lays
// the candidates side by side with everything needed to judge them — each
// route's confidence, its caveat, and a link to the page it came from.
function ConflictDialog({ field, onPick, onClose }) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    ref.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const candidates = (field.alternatives || []).filter((a) => a.value !== null);
  return (
    <div className="ex-detail-backdrop" onMouseDown={onClose}>
      <div
        ref={ref}
        tabIndex={-1}
        className="ex-cdlg"
        role="dialog"
        aria-modal="true"
        aria-label={`הכרעה בסתירה — ${field.displayName}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header>
          <h4><i className="fi fi-rr-triangle-warning" /> {field.displayName} — ערכים סותרים</h4>
          <button type="button" className="ex-detail-x" onClick={onClose}>
            <i className="fi fi-rr-cross-small" /> סגור
          </button>
        </header>
        <p className="ex-cdlg-sub">
          {candidates.length} מסלולים הגיעו לערכים שונים. בחרו את המסלול הקובע — הבחירה נשמרת ותיפתח מחדש אם מסלול יופעל שוב ויחזיר ערך אחר.
        </p>
        <ul className="ex-cdlg-list">
          {candidates.map((c) => {
            const r = routeById(c.routeId);
            const on = c.routeId === field.selectedRouteId;
            return (
              <li key={c.routeId} className={`ex-cdlg-item ${on ? 'is-on' : ''}`}>
                <button type="button" className="ex-cdlg-pick" onClick={() => onPick(field, c.routeId)}>
                  <span className="ex-cand-radio" />
                  <span className="ex-cdlg-val">{fmt(c.value, c.unit)}</span>
                  <span className="ex-cdlg-meta">
                    <span className="ex-cdlg-route">{r?.name || c.routeId}</span>
                    <span className="ex-cdlg-conf">
                      ודאות {(CONFIDENCE_LABEL[c.confidence] || CONFIDENCE_LABEL.missing).label}
                      {c.source?.label ? ` · ${c.source.label}` : ''}
                    </span>
                    {c.note && <span className="ex-cdlg-note">{c.note}</span>}
                  </span>
                </button>
                {c.source?.link && (
                  <a className="ex-cdlg-open" href={c.source.link} target="_blank" rel="noreferrer">
                    <i className="fi fi-rr-arrow-up-right-from-square" /> מקור
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// ─── Consolidation table (§8) ───────────────────────────────────────────────
function ValueRow({ field, routes, flash, onPickRoute, onEdit, onApprove, onUnapprove, onNote, onGoFill, onReset, onResolve }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const st = VALUE_STATUS[field.status] || VALUE_STATUS.missing;
  const withValues = (field.alternatives || []).filter((a) => a.value !== null);

  return (
    <div className={`ex-val-row ${field.status === 'conflict' ? 'is-conflict' : ''} ${flash ? 'is-flash' : ''}`} data-field-id={field.fieldId}>
      <span className="ex-val-name">
        {field.displayName}
        {field.required && <span className="ex-req" title="נתון חובה">*</span>}
        {field.note && (
          <span className="ex-val-note" title={field.note}>
            <i className="fi fi-rr-comment" /> {field.note}
          </span>
        )}
      </span>

      <span className="ex-val-value">
        {editing ? (
          <input
            autoFocus
            value={draft}
            // Select on focus: the editor seeds with the current value, so
            // typing a replacement appended to it instead.
            onFocus={(e) => e.target.select()}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => { setEditing(false); if (draft.trim()) onEdit(field, draft.trim()); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { setEditing(false); if (draft.trim()) onEdit(field, draft.trim()); }
              if (e.key === 'Escape') setEditing(false);
            }}
          />
        ) : (
          <button
            type="button"
            className="ex-val-btn"
            aria-label={`ערוך את ${field.displayName}`}
            title={`ערוך את ${field.displayName}`}
            onClick={() => { setDraft(field.selectedValue === null ? '' : String(field.selectedValue)); setEditing(true); }}
          >
            {fmt(field.selectedValue, field.unit)}
          </button>
        )}
      </span>

      <span className="ex-val-route">
        <select
          value={field.selectedRouteId || ''}
          aria-label={`מסלול מקור עבור ${field.displayName}`}
          onChange={(e) => onPickRoute(field, e.target.value)}
        >
          {/* Selectable, not disabled: picking a route used to be a one-way door
              with no way back to the system's own precedence. */}
          <option value="">בחירה אוטומטית (ברירת מחדל)</option>
          {/* The selected value has its own column (§8), so the option carries
              the route name alone — appending the value truncated every label.
              Only routes that CAN'T answer are annotated, since that's what the
              name doesn't already tell you. */}
          {ROUTES.filter((r) => (fieldDef(field.fieldId)?.routes || []).includes(r.id)).map((r) => {
            const obs = (field.alternatives || []).find((a) => a.routeId === r.id);
            const suffix = obs ? (obs.value !== null ? '' : ' — אין ערך') : ' — לא הופעל';
            return <option key={r.id} value={r.id}>{r.name}{suffix}</option>;
          })}
        </select>
        {/* A labelled chip rather than a bare icon, and the slot is always
            present so the select is the same width on every row. */}
        {field.selectedSource?.link ? (
          <a className="ex-val-srclink" href={field.selectedSource.link} target="_blank" rel="noreferrer">
            <i className="fi fi-rr-arrow-up-right-from-square" /> מקור
          </a>
        ) : (
          <span className="ex-val-srclink is-empty" aria-hidden="true" />
        )}
      </span>

      <span className="ex-val-status">
        <Pill tone={st.tone}>{st.label}</Pill>
        {/* Confidence qualifies the selected value, so it reads as a caption on
            the status rather than earning a column of its own — six columns
            stopped fitting once a row could carry three action chips. */}
        {field.selectedValue !== null && field.status !== 'conflict' && (
          <span className="ex-val-conf">ודאות: {(CONFIDENCE_LABEL[field.confidence] || CONFIDENCE_LABEL.missing).label}</span>
        )}
        {field.status === 'conflict' && (
          <span className="ex-conflict-note">
            {withValues.map((a) => fmt(a.value, '')).join(' · ')}
          </span>
        )}
      </span>

      <span className="ex-val-actions">
        {/* The link the screen was missing: from a datum that still blocks the
            stage, straight to a route that could supply it. Required fields
            only — on every missing row it became 20 identical chips and stopped
            pointing anywhere in particular. */}
        {field.selectedValue === null && field.required && (() => {
          const target = routesForField(field.fieldId, routes)[0];
          if (!target) return null;
          return (
            <button type="button" className="ex-act is-fill" onClick={() => onGoFill(target.id)} title={target.name}>
              <i className="fi fi-rr-arrow-up-right-from-square" /> השלם
            </button>
          );
        })()}
        {field.status === 'conflict' && (
          <button type="button" className="ex-act is-conflict" onClick={() => onResolve(field)}>
            <i className="fi fi-rr-triangle-warning" /> הכריעו
          </button>
        )}
        {field.selectedValue !== null && field.status !== 'approved' && field.status !== 'conflict' && (
          <button type="button" className="ex-act" onClick={() => onApprove(field)}><i className="fi fi-rr-check" /> אשר</button>
        )}
        {field.status === 'approved' && (
          <button type="button" className="ex-act" onClick={() => onUnapprove(field)}>
            <i className="fi fi-rr-rotate-left" /> בטל אישור
          </button>
        )}
        {/* Reset is only meaningful where the user intervened; on a plain
            sourced value you switch routes instead. */}
        {(field.status === 'user_input' || field.status === 'override') && (
          <button type="button" className="ex-act is-reset" onClick={() => onReset(field)}>
            <i className="fi fi-rr-rotate-left" /> אפס
          </button>
        )}
        <button type="button" className={`ex-act ${field.note ? 'is-on' : ''}`} onClick={() => onNote(field)}>
          <i className="fi fi-rr-comment" /> {field.note ? 'ערוך הערה' : 'הערה'}
        </button>
      </span>
    </div>
  );
}

// ─── Schematic (§10) ────────────────────────────────────────────────────────
function Schematic({ schematic, fields, elements, onAssign }) {
  const [detail, setDetail] = useState(null);
  // Both sibling dialogs on this screen close on Escape; this one didn't.
  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDetail(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [detail]);

  if (!schematic.ready) {
    return (
      <p className="ex-schema-empty">
        <i className="fi fi-rr-info" /> הסכמה תיבנה לאחר שייבחר ערך למספר הקומות ולשאר נתוני החובה.
      </p>
    );
  }

  const srcOf = (id) => fields.find((f) => f.fieldId === id)?.selectedSource?.label || '—';
  const byFloor = new Map();
  for (const el of elements) {
    if (!byFloor.has(el.floorId)) byFloor.set(el.floorId, []);
    byFloor.get(el.floorId).push(el);
  }
  const placement = orientationProgress(elements);

  const cell = (el) => {
    const t = ELEMENT_TYPES[el.type] || ELEMENT_TYPES.apartment;
    return (
      <button
        type="button"
        key={el.id}
        className={`ex-el ex-el-${t.tone}`}
        onClick={() => setDetail({ ...el, typeLabel: t.label })}
      >
        <span className="ex-el-name">{el.name}</span>
        {el.area !== null && el.area !== undefined && (
          <span className="ex-el-area">{fmt(el.area, 'מ״ר')}{el.derived ? ' ~' : ''}</span>
        )}
      </button>
    );
  };

  return (
    <div className="ex-schema">
      {placement.total > 0 && placement.placed < placement.total && (
        <p className="ex-schema-hint">
          <i className="fi fi-rr-info" /> {placement.placed} מתוך {placement.total} יחידות שויכו לחזית.
          לחצו על יחידה כדי לקבוע את החזית שאליה היא פונה — אין מסלול שיכול להסיק זאת.
        </p>
      )}

      {/* Facade × floor: the columns are the building's sides, and a unit sits in
          the column it faces. A unit whose facade nobody has determined stays in
          the unassigned band rather than being placed by guesswork. */}
      <div className="ex-matrix" style={{ '--facades': FACADES.length }}>
        <div className="ex-mx-head">קומות</div>
        {FACADES.map((f) => <div key={f.id} className="ex-mx-head ex-mx-facade">{f.name}</div>)}
        <div className="ex-mx-head">סיכומים לפי קומה</div>

        {schematic.floors.map((floor) => {
          const els = byFloor.get(floor.id) || [];
          const facing = els.filter(isFacadePlaceable);
          const spanning = els.filter((e) => !isFacadePlaceable(e));
          const unplaced = facing.filter((e) => !e.orientation);
          return (
            <React.Fragment key={floor.id}>
              <div className="ex-gcell ex-gfloor">
                <span className="ex-floor-num">{floor.level === null ? '' : floor.level}</span>
                <span className="ex-floor-name">{floor.name}</span>
              </div>

              {FACADES.map((f) => (
                <div key={f.id} className="ex-gcell ex-mx-cell">
                  {facing.filter((e) => e.orientation === f.id).map(cell)}
                </div>
              ))}

              <div className="ex-gcell ex-gsum">
                <span>{floor.name}</span>
                <span>
                  {floor.units ? `${floor.units} יח׳` : ''}
                  {floor.units && floor.area ? ', ' : ''}
                  {floor.area ? fmt(floor.area, 'מ״ר') : ''}
                  {!floor.units && !floor.area ? '—' : ''}
                </span>
              </div>

              {/* Whole-building bands span the row — a roof or a parking level
                  does not face a side, so it is never offered one. */}
              {spanning.length > 0 && (
                <div className="ex-mx-span">{spanning.map(cell)}</div>
              )}
              {unplaced.length > 0 && (
                <div className="ex-mx-unplaced">
                  <span className="ex-mx-unplaced-label">ללא שיוך לחזית</span>
                  {unplaced.map(cell)}
                </div>
              )}
            </React.Fragment>
          );
        })}

        <div className="ex-gcell ex-gsum is-total" style={{ gridColumn: FACADES.length + 2 }}>
          <span>סה״כ</span>
          <span>
            {schematic.totals.units ? `${schematic.totals.units} יח׳` : ''}
            {schematic.totals.units && schematic.totals.area ? ', ' : ''}
            {schematic.totals.area ? fmt(schematic.totals.area, 'מ״ר') : ''}
          </span>
        </div>
      </div>

      <div className="ex-legend">
        <span className="ex-legend-note">הערה: לחיצה על תא פותחת פרטי יחידה מלאים</span>
        {Object.entries(ELEMENT_TYPES).map(([k, t]) => (
          <span key={k} className="ex-legend-item"><i className={`ex-swatch ex-el-${t.tone}`} /> {t.label}</span>
        ))}
      </div>

      {detail && (
        <div className="ex-detail-backdrop" onMouseDown={() => setDetail(null)}>
          <div
            className="ex-detail"
            role="dialog"
            aria-modal="true"
            aria-label={`פרטי ${detail.name}`}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <header>
              <h4>{detail.name}</h4>
              <button type="button" className="ex-detail-x" onClick={() => setDetail(null)}>
                <i className="fi fi-rr-cross-small" /> סגור
              </button>
            </header>
            <dl>
              <div><dt>סוג אלמנט</dt><dd>{detail.typeLabel}</dd></div>
              <div><dt>קומה</dt><dd>{detail.floorName}</dd></div>
              <div><dt>שטח</dt><dd>{fmt(detail.area, 'מ״ר')}</dd></div>
              <div><dt>מקור השטח</dt><dd>{detail.sourceField ? srcOf(detail.sourceField) : '—'}</dd></div>
              {detail.derived && (
                <div><dt>אופן החישוב</dt><dd>מוערך — חלוקה של שטח הקומה, לא מדידה של האלמנט</dd></div>
              )}
            </dl>
            {isFacadePlaceable(detail) && (
            <label className="ex-detail-assign">
              <span>חזית</span>
              <select
                value={detail.orientation || ''}
                onChange={(e) => {
                  const orientation = e.target.value || null;
                  onAssign(detail.id, orientation);
                  setDetail((d) => ({ ...d, orientation }));
                }}
              >
                <option value="">לא נקבעה</option>
                {FACADES.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </label>
            )}
            {isFacadePlaceable(detail)
              ? <p className="ex-detail-note">שיוך לחזית הוא קביעה של המשתמש — אף מסלול אינו מספק אותו.</p>
              : <p className="ex-detail-note">אלמנט זה משתרע על כל רוחב הבניין ואינו פונה לחזית מסוימת.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Screen ─────────────────────────────────────────────────────────────────
export default function ExistingCondition({ project, onBack, onNext, onPrev, onGoStage }) {
  const [vm, setVm] = useState(null);
  const [loaded, setLoaded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openRoute, setOpenRoute] = useState(null);
  const [running, setRunning] = useState(null);
  const [liveLog, setLiveLog] = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);
  const [noteFor, setNoteFor] = useState(null);
  const [resetFor, setResetFor] = useState(null);
  const [conflictFor, setConflictFor] = useState(null);
  // The manual route's in-progress form, held here so collapsing the panel or
  // editing the table below doesn't discard it.
  const [manualDraft, setManualDraft] = useState({});
  // Rows whose value just changed, so the consolidation shows what a run did.
  const [flash, setFlash] = useState([]);
  // One-line summary of the last run, in terms of the stage's actual goal.
  const [lastRun, setLastRun] = useState(null);
  const name = project?.name || 'פרויקט';

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!project?.id) { setLoading(false); return; }
      setLoading(true);
      try {
        const full = await getProject(project.id);
        if (!alive) return;
        setLoaded(full);
        // The routes read BOTH the chunk-02 snapshot and the chunk-03 reviewed
        // layer, so a value the user approved or corrected in the statutory
        // stage carries forward instead of being re-derived from raw OCR.
        const ctx = {
          identification: full.project?.identification || null,
          statutory: full.project?.statutory || null,
        };
        const sig = propertySignature(full.project?.property || project.property);
        const want = exPropKey(sig);
        const saved = full.project?.existing;
        const fresh = saved && saved.builtVersion === EX_VERSION && exPropKey(saved.signature) === want;
        // Out of date but for the SAME property — carry its documents and
        // decisions across instead of discarding the user's work.
        const priorForProperty = saved && exPropKey(saved.signature) === want ? saved : null;
        if (fresh) setVm(saved);
        else {
          const built = buildExisting(ctx, sig, priorForProperty);
          setVm(built);
          saveExisting(project.id, built).catch(() => setError(SAVE_FAILED));
        }
      } catch {
        if (alive) setError('טעינת ניתוח המצב הקיים נכשלה. נסו שוב.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  // Persisting from inside a setState updater made the updater impure (React
  // invokes it twice in StrictMode and may re-invoke it on a concurrent render),
  // and every failure was swallowed. Mark dirty, save in an effect, surface the
  // error — losing a stage's work silently is the worst possible outcome here.
  const dirtyRef = useRef(false);
  const summaryRef = useRef(null);
  const markDirty = () => { dirtyRef.current = true; };

  useEffect(() => {
    if (!vm || !project?.id || !dirtyRef.current) return;
    dirtyRef.current = false;
    saveExisting(project.id, vm)
      .then(() => setError((e) => (e === SAVE_FAILED ? '' : e)))
      .catch(() => setError(SAVE_FAILED));
  }, [vm, project?.id]);

  // Flush the post-run summary after the state it describes has committed.
  useEffect(() => {
    if (!summaryRef.current) return;
    const { text, changed, gained, ids } = summaryRef.current;
    summaryRef.current = null;
    setFlash(ids);
    setLastRun({ text, changed, gained });
  }, [vm]);

  // Any change to the routes re-derives the consolidation from them, preserving
  // the user's per-field decisions (§8).
  // `fn` receives the CURRENT routes, so a mutation that lands while an await is
  // in flight can no longer be computed from a stale render closure.
  const mutateRoutes = (fn, logEvents = []) => {
    markDirty();
    setVm((prev) => {
      if (!prev) return prev;
      const routes = fn(prev.routes || []);
      return {
        ...prev,
        routes,
        fields: consolidate(routes, prev.fields),
        log: [...(prev.log || []), ...logEvents],
      };
    });
  };

  // A run's result stated in terms of the consolidation, not raw observation
  // counts: which rows actually changed, and whether that moved the §13 gate.
  // Without this, running a route changes rows far below the fold silently.
  const applyRunResult = (routeId, done, route) => {
    markDirty();
    setVm((prev) => {
      if (!prev) return prev;
      const routes = (prev.routes || []).map((r) => (r.id === routeId ? done : r));
      const before = prev.fields || [];
      const nextFields = consolidate(routes, before);
      const changed = nextFields.filter((f) => {
        const b = before.find((x) => x.fieldId === f.fieldId);
        return (b?.selectedValue ?? null) !== (f.selectedValue ?? null);
      });
      const gained = requiredProgress(nextFields).answered - requiredProgress(before).answered;
      const text = changed.length
        ? `${route.name} — עודכנו ${changed.length} נתונים${gained > 0 ? `, מתוכם ${gained} נתוני חובה` : ''}`
        : `${route.name} — לא שינה אף נתון בריכוז`;
      summaryRef.current = { text, changed: changed.length, gained, ids: changed.map((f) => f.fieldId) };
      return { ...prev, routes, fields: nextFields, log: [...(prev.log || []), exLogEvent(text, changed.length ? 'done' : 'warn')] };
    });
  };

  // Seed the manual draft from the saved run the first time its panel opens, so
  // reopening shows what was submitted rather than an empty form. Never re-seeds
  // afterwards — that is what used to wipe in-progress typing.
  const seededRef = useRef(false);
  useEffect(() => {
    if (openRoute !== 'manual_input' || seededRef.current) return;
    const run = (vm?.routes || []).find((r) => r.id === 'manual_input')?.run;
    if (!run) return;
    seededRef.current = true;
    const m = {};
    for (const o of run.observations || []) if (o.value !== null) m[o.fieldId] = String(o.value);
    setManualDraft((d) => ({ ...m, ...d }));
  }, [openRoute, vm]);

  // Clear the highlight once it has done its job.
  useEffect(() => {
    if (!flash.length) return undefined;
    const t = setTimeout(() => setFlash([]), 4000);
    return () => clearTimeout(t);
  }, [flash]);

  const snapshot = useMemo(() => ({
    identification: loaded?.project?.identification || null,
    statutory: loaded?.project?.statutory || null,
  }), [loaded]);

  const runRoute = async (routeId) => {
    const route = (vm?.routes || []).find((r) => r.id === routeId);
    if (!route) return;
    setRunning(routeId);
    setLiveLog((l) => [...l, exLogEvent(`מריץ מסלול: ${route.name}`, 'info')]);
    // The GIS route is the one that reaches outside — measure the footprint off
    // the buildings layer now rather than on load, so we only pay for it when
    // the user actually runs the route.
    let ctx = snapshot;
    if (routeId === 'gis_layers') {
      const gis = await fetchBuildingFootprint(snapshot.identification?.parcel);
      ctx = { ...snapshot, gis };
    } else {
      // Yield a frame so the spinner paints before the synchronous derivation.
      await new Promise((r) => setTimeout(r, 60));
    }
    try {
      const done = executeRoute(route, ctx);
      applyRunResult(routeId, done, route);
      setOpenRoute(routeId);
    } catch {
      setError('הרצת המסלול נכשלה. נסו שוב.');
    } finally {
      // Without this, any throw left the button disabled on a permanent
      // "מריץ…" spinner, recoverable only by reloading the page.
      setLiveLog([]);
      setRunning(null);
    }
  };

  // Adding/removing a document invalidates a completed run (§5): the route goes
  // back to "דורש הרצה מחדש" so a stale observation can't quietly stay selected.
  // Changing a route's evidence DISCARDS its results. Previously the status
  // flipped to "דורש הרצה מחדש" while the observations stayed, so the
  // consolidation kept showing values sourced to a document that was no longer
  // attached — the comment claimed otherwise and the code did not do it.
  const setDocs = (routeId, fn, logText) => mutateRoutes(
    (routes) => routes.map((r) => {
      if (r.id !== routeId) return r;
      const documents = fn(r.documents || []);
      const status = (r.kind === 'document' && !documents.length) ? 'missing_docs' : 'ready';
      return { ...r, documents, status, run: null };
    }),
    [exLogEvent(logText, 'info')]
  );

  const addDoc = (routeId, doc) => setDocs(routeId, (d) => [...d, doc], `צורף מסמך: ${doc.name} — תוצאות המסלול אופסו, יש להריץ מחדש`);
  const removeDoc = (routeId, idx) => setDocs(routeId, (d) => d.filter((_, i) => i !== idx), 'הוסר מסמך מהמסלול — תוצאות המסלול אופסו, יש להריץ מחדש');

  // §7.1 — the manual route's "run" is the user's own submission.
  const submitManual = (vals) => {
    const observations = ALL_FIELDS
      .filter((f) => f.routes.includes('manual_input'))
      .map((f) => {
        const raw = vals[f.id];
        const has = raw !== undefined && String(raw).trim() !== '';
        let value = null;
        if (has) {
          if (f.type === 'number') value = Number(raw);
          else if (f.type === 'bool') value = raw === 'true';
          else value = String(raw).trim();
        }
        return {
          routeId: 'manual_input',
          fieldId: f.id,
          fieldName: f.id,
          displayName: f.name,
          value,
          unit: f.unit,
          source: { label: 'הזנה ידנית', link: null, database: null, dataset: null },
          confidence: 'user_defined',
          status: has ? 'observed' : 'not_found',
          note: '',
        };
      });
    const found = observations.filter((o) => o.value !== null).length;
    const manual = (vm?.routes || []).find((r) => r.id === 'manual_input');
    const done = {
      ...(manual || {}),
      status: found ? 'completed' : 'missing_docs',
      run: {
        runId: `manual-${Date.now()}`,
        status: found ? 'completed' : 'partial',
        usedDocuments: (manual?.documents || []).map((d) => d.name),
        observations,
        lastRunAt: new Date().toISOString(),
      },
    };
    applyRunResult('manual_input', done, manual || { name: 'הזנה ידנית' });
  };

  // Per-field decisions (§8 / §12).
  const mutateField = (fieldId, patch, action) => { markDirty(); return setVm((prev) => {
    if (!prev) return prev;
    const at = new Date().toISOString();
    const next = {
      ...prev,
      fields: prev.fields.map((f) => (f.fieldId === fieldId
        ? {
          ...f,
          ...(typeof patch === 'function' ? patch(f) : patch),
          lastUpdated: at,
          history: [...(f.history || []), { at, action, from: f.selectedValue ?? null, to: (typeof patch === 'function' ? patch(f) : patch).selectedValue ?? f.selectedValue ?? null }],
        }
        : f)),
    };
    return next;
  }); };

  const pickRoute = (field, routeId) => {
    markDirty();
    // Back to automatic: drop the pin and let precedence choose again.
    if (!routeId) {
      setVm((prev) => {
        if (!prev) return prev;
        const at = new Date().toISOString();
        const priors = (prev.fields || []).map((f) => (f.fieldId === field.fieldId ? {
          ...f,
          selectedRouteId: null,
          userResolution: null,
          status: 'check',
          history: [...(f.history || []), { at, action: 'auto_select', from: f.selectedValue ?? null, to: null }],
        } : f));
        return { ...prev, fields: consolidate(prev.routes || [], priors) };
      });
      return;
    }
    const obs = (field.alternatives || []).find((a) => a.routeId === routeId);
    mutateField(field.fieldId, {
      selectedRouteId: routeId,
      selectedValue: obs && obs.value !== null ? obs.value : null,
      selectedSource: obs?.source || null,
      confidence: obs?.confidence || 'missing',
      // Picking a route whose run produced nothing is a legitimate state — the
      // row reports "חסר במסלול הנבחר" and blocks completion until resolved.
      status: obs && obs.value !== null ? 'check' : 'not_found',
      conflict: false,
      // Bind the pin to the values it was chosen among, so a later re-run with
      // different numbers re-opens the decision.
      userResolution: { routeId, at: new Date().toISOString(), altSig: field.altSig },
    }, 'pick_route');
  };

  // Typing a value here IS a manual-entry observation, so it is recorded on that
  // route rather than floating free: the row previously showed a value next to
  // "בחרו מסלול…", and the manual route never learned about it, so section 1's
  // counts and its form disagreed with the table below.
  //
  // The STATUS still distinguishes the two cases the v4 model separates —
  // replacing a sourced value is an Override (the other routes' observations
  // stay available to switch back to), filling an empty one is User Input — but
  // the ROUTE is the manual one either way, because that is where the number
  // actually came from.
  const editValue = (field, raw) => {
    const def = fieldDef(field.fieldId);
    let value = raw;
    if (def?.type === 'number') {
      // Accept the Israeli thousands separator, but refuse anything that isn't a
      // number: "1,200" used to be stored as text, satisfy the completion gate,
      // and then read as null by everything downstream.
      const n = Number(String(raw).replace(/,/g, '').trim());
      if (!Number.isFinite(n)) {
        setError(`«${field.displayName}» הוא נתון מספרי — הערך «${raw}» לא נשמר.`);
        return;
      }
      value = n;
    } else if (def?.type === 'bool') value = raw === 'true' || raw === 'כן';
    markDirty();
    const status = (field.selectedValue !== null && field.selectedSource) ? 'override' : 'user_input';
    // The manual form rebuilds its route's observations wholesale from this
    // draft, so a value typed in the table has to land here too — otherwise the
    // next submit of that form silently writes it back as null.
    setManualDraft((d) => ({ ...d, [field.fieldId]: String(value) }));

    setVm((prev) => {
      if (!prev) return prev;
      const at = new Date().toISOString();
      const routes = (prev.routes || []).map((r) => {
        if (r.id !== 'manual_input') return r;
        const obs = [...(r.run?.observations || [])];
        const entry = {
          routeId: 'manual_input',
          fieldId: field.fieldId,
          fieldName: field.fieldId,
          displayName: field.displayName,
          value,
          unit: field.unit,
          source: { label: 'הזנה ידנית', link: null, database: null, dataset: null },
          confidence: 'user_defined',
          status: 'observed',
          note: '',
        };
        const i = obs.findIndex((o) => o.fieldId === field.fieldId);
        if (i >= 0) obs[i] = entry; else obs.push(entry);
        return {
          ...r,
          status: 'completed',
          run: {
            runId: r.run?.runId || `manual-${at}`,
            status: 'completed',
            usedDocuments: (r.documents || []).map((d) => d.name),
            observations: obs,
            lastRunAt: at,
          },
        };
      });
      const fields = (prev.fields || []).map((f) => (f.fieldId === field.fieldId ? {
        ...f,
        selectedValue: value,
        selectedRouteId: 'manual_input',
        selectedSource: { label: 'הזנה ידנית', link: null },
        confidence: 'user_defined',
        status,
        conflict: false,
        lastUpdated: at,
        history: [...(f.history || []), { at, action: status, from: f.selectedValue ?? null, to: value }],
      } : f));
      return { ...prev, routes, fields: consolidate(routes, fields) };
    });
  };

  const approve = (field) => mutateField(field.fieldId, { status: 'approved' }, 'approve');
  const unapprove = (field) => mutateField(field.fieldId, { status: 'check' }, 'unapprove');

  // A unit's facade is user knowledge, so it is stored on the element and
  // survives every re-derivation of the schematic.
  const assignFacade = (elementId, orientation) => {
    markDirty();
    setVm((prev) => {
      if (!prev) return prev;
      const current = buildElements(prev.fields || [], prev.elements || []);
      return {
        ...prev,
        elements: current.map((e) => (e.id === elementId ? { ...e, orientation } : e)),
      };
    });
  };

  // Undo a manual intervention and let the routes speak again: drops the
  // manual-entry observation and clears the row's decision, so consolidation
  // re-picks by precedence. If no route can answer, the row returns to חסר.
  // The history is kept — a reset is another audited action, not an erasure.
  const resetField = (field) => {
    markDirty();
    setManualDraft((d) => { const n = { ...d }; delete n[field.fieldId]; return n; });
    setVm((prev) => {
      if (!prev) return prev;
      const at = new Date().toISOString();
      const routes = (prev.routes || []).map((r) => {
        if (r.id !== 'manual_input' || !r.run) return r;
        return { ...r, run: { ...r.run, observations: (r.run.observations || []).filter((o) => o.fieldId !== field.fieldId) } };
      });
      const priors = (prev.fields || []).map((f) => (f.fieldId === field.fieldId ? {
        ...f,
        selectedValue: null,
        selectedRouteId: null,
        selectedSource: null,
        confidence: 'missing',
        status: 'missing',
        userResolution: null,
        note: f.note,
        lastUpdated: at,
        history: [...(f.history || []), { at, action: 'reset', from: f.selectedValue ?? null, to: null }],
      } : f));
      return { ...prev, routes, fields: consolidate(routes, priors) };
    });
    setResetFor(null);
  };

  // What the row will show after a reset, so the confirmation can say so.
  const resetOutcome = (field) => {
    const alt = (field.alternatives || [])
      .filter((o) => o.routeId !== 'manual_input' && o.value !== null);
    if (!alt.length) return null;
    const best = routesForField(field.fieldId, vm?.routes || [])
      .filter((r) => r.id !== 'manual_input')
      .map((r) => alt.find((o) => o.routeId === r.id))
      .find(Boolean) || alt[0];
    return { value: best.value, unit: best.unit, routeName: routeById(best.routeId)?.name };
  };

  const handleSaveDraft = async () => {
    if (!project?.id) { onBack?.(); return; }
    setSavingDraft(true);
    try { await updateProject(project.id, { status: 'draft' }); onBack?.(); }
    catch { setError('שמירת הטיוטה נכשלה. נסו שוב.'); setSavingDraft(false); }
  };

  const archiveDocs = (loaded?.documents || []).map((d) => ({
    name: d.file_name || d.document_type || 'מסמך',
    link: (d.file_id && /^https?:\/\//.test(d.file_id)) ? d.file_id : null,
  }));

  const fields = useMemo(() => vm?.fields || [], [vm]);
  const schematic = useMemo(() => buildSchematic(fields), [fields]);
  // Elements are rebuilt from the selected values but keep whatever the user
  // assigned, matched on a stable id.
  const elements = useMemo(() => buildElements(fields, vm?.elements || []), [fields, vm]);
  const completion = useMemo(() => (vm ? completionState(vm) : null), [vm]);
  const progress = useMemo(() => requiredProgress(fields), [fields]);
  const log = [...(vm?.log || []), ...liveLog];

  // Jump from the goal strip to the row that needs attention, and flash it so
  // it is obvious which one was meant.
  const goToField = (fieldId) => {
    setFlash([fieldId]);
    window.requestAnimationFrame(() => {
      document.querySelector(`[data-field-id="${fieldId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  // Jump from a blocking field to the route that could answer it.
  const goFill = (routeId) => {
    setOpenRoute(routeId);
    setLastRun(null);
    window.requestAnimationFrame(() => {
      document.querySelector(`[data-route-id="${routeId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const goNext = () => { if (completion?.ok) onNext?.(project); };

  return (
    <div className="ex">
      <nav className="np-crumbs">
        <button type="button" onClick={onBack}>פרויקטים</button>
        <i className="fi fi-rr-angle-small-left" />
        <span className="sa-crumb-name">{name}</span>
        <i className="fi fi-rr-angle-small-left" />
        <span>ניתוח מצב קיים</span>
      </nav>

      <header className="pid-workhead">
        <div className="pid-workhead-text">
          <h1>{name}</h1>
          <p>הרצת מסלולי הסקה על הראיות שנאספו, בחירת ערך נבחר לכל נתון, ובניית סכמת המצב הקיים.</p>
        </div>
        <div className="pid-workhead-actions">
          <button type="button" className="np-btn np-btn-secondary" onClick={() => onPrev?.(project)} disabled={loading}>
            <i className="fi fi-rr-angle-small-right" /> הקודם
          </button>
          <button type="button" className="np-btn np-btn-secondary" onClick={handleSaveDraft} disabled={loading || savingDraft}>
            <i className="fi fi-rr-bookmark" /> {savingDraft ? 'שומר…' : 'שמירת טיוטה'}
          </button>
          {/* §8/§13 are a hard gate: nothing downstream may receive a
              half-filled existing condition. "שמירת טיוטה" is the way to park a
              project you can't finish today. */}
          <button
            type="button"
            className="np-btn np-btn-primary"
            onClick={goNext}
            disabled={loading || !vm || !completion?.ok}
            title={completion && !completion.ok ? blockReason(completion, progress) : undefined}
          >
            סיום השלב <i className="fi fi-rr-angle-small-left" />
          </button>
        </div>
      </header>

      {error && <div className="pid-error" role="alert"><i className="fi fi-rr-exclamation" /> {error}</div>}

      {/* Why the stage can't be finished, next to the button that can't be
          pressed — a disabled control with no stated reason is a dead end. */}
      {completion && !completion.ok && !loading && (
        <p className="ex-blocked"><i className="fi fi-rr-lock" /> {blockReason(completion, progress)}</p>
      )}

      <div className="pid-layout">
        <aside className="pid-rail">
          <AgentActivity log={log} loading={loading} />
          <Roadmap
            activeStage={ACTIVE_STAGE}
            reachedStage={Math.max(ACTIVE_STAGE, reachedStageFor(loaded?.project))}
            onStageClick={(i) => onGoStage?.(i, { loaded, project })}
          />
          <DocumentArchive inline projectId={project?.id} />
        </aside>

        <div className="pid-work">
          <div className="sa-collected">
            <i className="fi fi-rr-info" />
            <span>המערכת זיהתה {ROUTES.length} מסלולי הסקה אפשריים למצב הקיים. הפעילו מסלולים, בחרו ערך נבחר לכל נתון, והסכמה תיבנה מהערכים שנבחרו.</span>
          </div>

          {/* What still stands between here and the next stage. The completion
              gate already knew all of this; it just never reached the screen. */}
          {completion && (
            <section className={`ex-goal ${completion.ok ? 'is-ok' : ''}`}>
              <div
                className="ex-goal-bar"
                role="progressbar"
                aria-label="נתוני חובה שהושלמו"
                aria-valuemin={0}
                aria-valuemax={progress.total}
                aria-valuenow={progress.answered}
              >
                <span className="ex-goal-fill" style={{ width: `${progress.total ? (progress.answered / progress.total) * 100 : 0}%` }} />
              </div>
              <div className="ex-goal-items">
                <span className={`ex-goal-item ${progress.answered === progress.total ? 'is-done' : ''}`}>
                  <i className={`fi ${progress.answered === progress.total ? 'fi-rr-check' : 'fi-rr-circle'}`} />
                  {progress.answered} מתוך {progress.total} נתוני חובה הושלמו
                </span>
                <span className={`ex-goal-item ${completion.unresolvedConflicts.length ? 'is-bad' : (completion.atLeastOneRun ? 'is-done' : '')}`}>
                  <i className={`fi ${completion.unresolvedConflicts.length ? 'fi-rr-triangle-warning' : (completion.atLeastOneRun ? 'fi-rr-check' : 'fi-rr-circle')}`} />
                  {completion.unresolvedConflicts.length === 0 && (completion.atLeastOneRun ? 'אין סתירות פתוחות' : 'טרם הופעל מסלול')}
                  {completion.unresolvedConflicts.length === 1 && 'סתירה אחת בין מסלולים להכרעה'}
                  {completion.unresolvedConflicts.length > 1 && `${completion.unresolvedConflicts.length} סתירות בין מסלולים להכרעה`}
                </span>
                <span className={`ex-goal-item ${completion.schematicReady ? 'is-done' : ''}`}>
                  <i className={`fi ${completion.schematicReady ? 'fi-rr-check' : 'fi-rr-circle'}`} />
                  {completion.schematicReady ? 'סכמת הבניין נבנתה' : 'סכמת הבניין טרם נבנתה'}
                </span>
              </div>

              {/* Naming a count is not enough — every outstanding item is listed
                  by field and jumps to its row, so "1 conflict" is answerable
                  without scrolling the table hunting for the pink one. */}
              {(progress.missing.length > 0 || completion.unresolvedConflicts.length > 0) && (
                <div className="ex-goal-todo">
                  {completion.unresolvedConflicts.map((f) => (
                    <button type="button" key={f.fieldId} className="ex-todo is-conflict" onClick={() => goToField(f.fieldId)}>
                      <i className="fi fi-rr-triangle-warning" /> {f.displayName}
                      <span className="ex-todo-why">סתירה</span>
                    </button>
                  ))}
                  {progress.missing.map((f) => (
                    <button type="button" key={f.fieldId} className="ex-todo" onClick={() => goToField(f.fieldId)}>
                      <i className="fi fi-rr-circle" /> {f.displayName}
                      <span className="ex-todo-why">חסר</span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {lastRun && (
            <div className={`ex-runsummary ${lastRun.changed ? '' : 'is-quiet'}`} role="status">
              <i className={`fi ${lastRun.changed ? 'fi-rr-check' : 'fi-rr-info'}`} />
              <span>{lastRun.text}</span>
              <button type="button" onClick={() => setLastRun(null)} aria-label="סגור"><i className="fi fi-rr-cross-small" /></button>
            </div>
          )}

          {/* ── 1. Routes list ── */}
          <section className="pid-card">
            <header className="pid-card-head">
              <span className="ex-step">1</span>
              <h3>רשימת מסלולים</h3>
              <span className="pid-count">{ROUTES.length}</span>
            </header>
            <div className="ex-routes">
              <div className="ex-route-row ex-route-head">
                <span /><span /><span>מסלול</span><span>מענה</span><span>סטטוס</span><span>פעולה</span>
              </div>
              {loading && !vm ? (
                <div className="sa-loading"><span className="rp-spinner" /> מכין את מסלולי ההסקה…</div>
              ) : (vm?.routes || []).map((r) => (
                <RouteRow
                  key={r.id}
                  route={r}
                  fields={fields}
                  isOpen={openRoute === r.id}
                  running={running}
                  archiveDocs={archiveDocs}
                  onToggle={(id, force) => setOpenRoute((p) => (force ? id : (p === id ? null : id)))}
                  onRun={runRoute}
                  onAddDoc={addDoc}
                  onRemoveDoc={removeDoc}
                  onManual={submitManual}
                  manualDraft={manualDraft}
                  onManualChange={setManualDraft}
                />
              ))}
            </div>
          </section>

          {/* ── 2. Consolidation ── */}
          <section className="pid-card">
            <header className="pid-card-head">
              <span className="ex-step">2</span>
              <h3>ריכוז נתונים</h3>
              {completion && completion.missingRequired.length > 0 && (
                <span className="ex-head-warn">
                  {completion.missingRequired.length === 1 ? 'נתון חובה אחד חסר' : `${completion.missingRequired.length} נתוני חובה חסרים`}
                </span>
              )}
            </header>
            {loading && !vm && (
              <div className="sa-loading"><span className="rp-spinner" /> טוען את ריכוז הנתונים…</div>
            )}
            {!loading && vm && !completion?.atLeastOneRun && (
              <p className="ex-zone-empty">
                <i className="fi fi-rr-info" /> טרם הופעל מסלול. הפעילו מסלול למעלה כדי למלא את הטבלה, או הזינו נתונים ידנית.
              </p>
            )}
            <div className="ex-values">
              <div className="ex-val-row ex-val-head">
                <span>נתון</span><span>ערך נבחר</span><span>מסלול / מקור</span><span>סטטוס</span><span>פעולות</span>
              </div>
              {FIELD_GROUPS.map((g) => {
                const rows = fields.filter((f) => f.groupId === g.id);
                if (!rows.length) return null;
                return (
                  <React.Fragment key={g.id}>
                    <div className="ex-val-grouphead"><span>{g.title}</span></div>
                    {rows.map((f) => (
                      <ValueRow
                        key={f.fieldId}
                        field={f}
                        routes={vm?.routes || []}
                        flash={flash.includes(f.fieldId)}
                        onPickRoute={pickRoute}
                        onEdit={editValue}
                        onApprove={approve}
                        onUnapprove={unapprove}
                        onGoFill={goFill}
                        onReset={(fl) => setResetFor(fl)}
                        onResolve={(fl) => setConflictFor(fl.fieldId)}
                        onNote={(fl) => setNoteFor({ fieldId: fl.fieldId, name: fl.displayName, value: fl.note || '' })}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
          </section>

          {/* ── 3. Schematic ── */}
          <section className="pid-card">
            <header className="pid-card-head">
              <span className="ex-step">3</span>
              <h3>סכמת מצב קיים</h3>
            </header>
            <Schematic schematic={schematic} fields={fields} elements={elements} onAssign={assignFacade} />
          </section>
        </div>
      </div>

      {noteFor && (
        <NoteDialog
          name={noteFor.name}
          value={noteFor.value}
          onSave={(text) => { mutateField(noteFor.fieldId, { note: text }, 'note'); setNoteFor(null); }}
          onClose={() => setNoteFor(null)}
        />
      )}

      {conflictFor && (() => {
        const f = fields.find((x) => x.fieldId === conflictFor);
        if (!f) return null;
        return (
          <ConflictDialog
            field={f}
            onPick={(fl, routeId) => { pickRoute(fl, routeId); setConflictFor(null); }}
            onClose={() => setConflictFor(null)}
          />
        );
      })()}

      {resetFor && (() => {
        const out = resetOutcome(resetFor);
        return (
          <ConfirmDialog
            title={`איפוס «${resetFor.displayName}»`}
            message={out
              ? `הערך שהוזן יימחק והנתון יחזור לערך מהמסלול «${out.routeName}» (${fmt(out.value, out.unit)}).`
              : 'הערך שהוזן יימחק. אף מסלול אחר לא ענה על הנתון, ולכן הוא יחזור לרשימת החוסרים.'}
            onDismiss={() => setResetFor(null)}
            actions={[
              { label: 'ביטול', tone: 'ghost', onClick: () => setResetFor(null) },
              { label: 'אפס את הנתון', tone: 'danger', onClick: () => resetField(resetFor) },
            ]}
          />
        );
      })()}

    </div>
  );
}
